import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Datetime from 'react-datetime';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { get, isEmpty } from 'lodash';
import moment from 'moment';

import Input from '../../../components/Form/Input';
import { Button } from '../../../components/Button';
import Validator from '../../../components/Validator/Validator';
import schema from './schema';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import style from './style.scss';
import Info from './../../../assets/icons/info.svg';
import Header from '../../../components/Header';
import { getOrConvertToPst } from '../../../utils/timezone';
import { checkPreviousAction, nextAction } from '../../../actions/workflow';

const PickPaymentDate = (props) => {
  const { validator: { setValues, values, errors, setErrors, validate, onChangeHandler } } = props;
  const workflow = useSelector((state) => state.workflow);
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [isValidFirstPaymentDate, setIsValidFirstPaymentDate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isModal, setIsModal] = useState(false);
  const { workflowtype } = params;
  const applicationId = get(workflow, 'attributes.entityId');

  useEffect(() => {
    setValues({
      ...values,
      firstPaymentDate: getOrConvertToPst().add(35, 'd').format('MM/DD/YYYY'),
    });
  }, []);

  useEffect(() => {
    const key = searchParams.get('key');

    if (!key) {
      navigate(`/fsl/application${window.location.search}`);
    } else if (!workflow || isEmpty(workflow)) {
      dispatch(checkPreviousAction({
        data: {},
        url: `/workflows/fsl/${key}/checkin`,
        success: (response) => {
          const routeUrl = response.data && response.data.url;
          navigate(routeUrl);
        },
        fail: () => {
          navigate('/fsl/application', { replace: true });
        },
      }));
    }
  }, []);

  const handleDateChange = (date) => {
    if (typeof date.format === 'function') {
      onChangeHandler('firstPaymentDate', date.format('MM/DD/YYYY'));
      setIsValidFirstPaymentDate(date.isAfter(getOrConvertToPst().add(34, 'd')) && date.isBefore(getOrConvertToPst().add(45, 'd')));
    } else {
      onChangeHandler('firstPaymentDate', date);
      setIsValidFirstPaymentDate(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (schema && validate(schema).isValid) {
      const key = searchParams.get('key');
      setIsLoading(true);
      dispatch(nextAction({
        data: {
          FirstPaymentDate: moment(values.firstPaymentDate).format('MM-DD-YYYY'),
        },
        url: `/workflows/${workflowtype}/${key}/next`,
        success: (response) => {
          setIsLoading(false);
          navigate(response.data.url);
        },
        fail: (error) => {
          setIsLoading(false);
          console.log(error);
          if (get(error, 'data.failure.errors.0.subject') === 'date') {
            setErrors({
              ...errors,
              firstPaymentDate: get(error, 'data.failure.errors.0.message'),
            })
          }
        },
      }));
    } else {
      setIsLoading(false);
    }
  };

  const toggleModal = () => {
    setIsModal(!isModal);
  };

  return (
    <div>
      <style>
        {`
          .modal.show{
            opacity: 1 !important
          }
          .modal-dialog{
            top: 200px;
          }
        `}
      </style>
      <Header />
      <div className={style.headingDiv}>
        {applicationId ? (
          <div className={style.left}>
            <p className="mb-0 p-large"><b>Application ID:</b> {applicationId}</p>
          </div>
        ) : null}
        <h4>PLEASE SELECT NEW PAYMENT DATE</h4>
        <div className={style.infoWrapper}>
          <h5>The payment date you have previously selected {' '} {get(workflow, 'data.data.firstPaymentDate') ? getOrConvertToPst(get(workflow, 'data.data.firstPaymentDate')).format('L') : ''} {' '} is no longer available.
          </h5>
          <img
            src={Info}
            alt="info icon"
            onClick={toggleModal}
          />
        </div>
        <div className={`${style.pickPaymentDateWrapper}, first-payment-date`}>
          <h6>Choose First Payment Date</h6>
          <p>Select a payment date which matches your payroll date.</p>
          <Datetime
            name="firstPaymentDate"
            label="First Payment Date"
            displayTimeZone="America/Los_Angeles"
            value={values.firstPaymentDate}
            defaultValue={getOrConvertToPst().add(35, 'd')}
            onChange={handleDateChange}
            closeOnSelect
            dateFormat="MM/DD/YYYY"
            timeFormat={false}
            isValidDate={current => (
              current.isAfter(getOrConvertToPst().add(34, 'd')) &&
                current.isBefore(getOrConvertToPst().add(45, 'd'))
            )}
            initialViewDate={getOrConvertToPst().add(35, 'd')}
            placeHolder="MM/DD/YYYY"
            renderInput={renderProps => (
              <Input
                isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                isRequired
                openCalendar={() => {}}
                hasError={!!errors.firstPaymentDate}
                errorMessage={errors.firstPaymentDate}
                label="First Payment Date"
                placeHolder="MM/DD/YYYY"
                labelClassName={cn({ 'iscompleted-data': !!isValidFirstPaymentDate })}
                {...renderProps}
              />
                          )}
          />
          <div className={style.confirmBtn}>
            <Button
              className="small button mt-2 green"
              type="button"
              onClick={handleSubmit}
              isLoading={isLoading}
            >
                CONFIRM
            </Button>
          </div>
        </div>
      </div>
      <Modal isOpen={isModal} toggle={toggleModal} size="lg" className={style.modal}>
        <ModalHeader toggle={toggleModal}>
        Why do I have to change my payment date?
        <button className="close-button" data-close aria-label="Close reveal" type="button" onClick={toggleModal}>
          <span aria-hidden="true">&times;</span>
        </button>
        </ModalHeader>
        <ModalBody style={{ maxHeight: 600, overflow: 'auto', opacity: '1 !important' }}>
          <div
            className="mb-0"
            style={{ fontSize: '0.85rem' }}
          >There must be at least
            {' '}
            {get(workflow, 'data.data.days') || ''}
            {' '}
          days between funding and the first payment date.
          </div>
          <div
            className="mb-0"
            style={{ fontSize: '0.85rem' }}
          >You will be able to adjust your first payment date by calling our servicing team after your loan has been funded. (needs to be rephrased)
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

PickPaymentDate.propTypes = {
  validator: PropTypes.object.isRequired,
};

export default Validator(schema)(PickPaymentDate);
